import React, { FC, MouseEvent, useEffect, useMemo, useState } from 'react'
import {
  container,
  activeSwitcher,
  inactiveSwitcher,
  reviewsPoint,
  endorsementsPoint,
  verticalDivider,
  verticalDividerHidden,
  truncateText,
  minimalViewText
} from './sortingRatingBar.css'
import { classNames } from '@hermes/design-system'
import { AreasSortingTabs, SortingOptions } from '../../../../constants/profile'

export type SortingRatingBarProps = {
  reviewsCount: number
  endorsementsCount: number
  showAlphabetExceptAll?: boolean
  handleAlphabetSort: () => void
  handleReviewsSort: () => void
  handleEndorsementsSort: () => void
  namingOptions: {
    alphabet: {
      az: string
      all: string
    }
    reviews: string
    endorsements: string
  }
  isFullView?: boolean
  sortingOptionToHide?: AreasSortingTabs | null
}

const getSortingOption = (sortingOptionToHide: AreasSortingTabs | null) => {
  if (sortingOptionToHide === null) {
    return SortingOptions.ALPHABET
  }

  const sortingOptions = {
    [AreasSortingTabs.REVIEWS]: SortingOptions.ENDORSEMENTS,
    [AreasSortingTabs.ENDORSEMENTS]: SortingOptions.REVIEWS
  }

  return sortingOptionToHide !== null && sortingOptionToHide in sortingOptions
    ? sortingOptions[sortingOptionToHide as keyof typeof sortingOptions]
    : SortingOptions.ALPHABET
}

const SortingRatingBar: FC<SortingRatingBarProps> = ({
  reviewsCount,
  endorsementsCount,
  showAlphabetExceptAll,
  handleAlphabetSort,
  handleReviewsSort,
  handleEndorsementsSort,
  namingOptions,
  isFullView = true,
  sortingOptionToHide
}) => {
  const [selectedSort, setSelectedSort] = useState<SortingOptions>(SortingOptions.ALPHABET)

  useEffect(() => {
    if (sortingOptionToHide) {
      setSelectedSort(getSortingOption(sortingOptionToHide))
    }
  }, [sortingOptionToHide])

  const onSelectSorting = (event: MouseEvent<HTMLDivElement>) => {
    const id = event.currentTarget.id
    if (selectedSort === id) {
      return
    }
    const onClickFunction = sortingOptions.find((option) => option.id === id)?.onClickFunction
    onClickFunction?.()
    setSelectedSort(id as SortingOptions)
  }

  const sortingOptions = useMemo(
    () => [
      {
        id: SortingOptions.ALPHABET,
        label: showAlphabetExceptAll ? namingOptions?.alphabet.az : namingOptions?.alphabet.all,
        onClickFunction: handleAlphabetSort
      },
      {
        id: SortingOptions.REVIEWS,
        label: (
          <>
            <div className={reviewsPoint} />
            <span className={classNames(truncateText, { [minimalViewText]: !isFullView })}>
              {namingOptions?.reviews}{' '}
            </span>
            ({reviewsCount})
          </>
        ),
        onClickFunction: handleReviewsSort
      },
      { id: 'divider' },
      {
        id: SortingOptions.ENDORSEMENTS,
        label: (
          <>
            <div className={endorsementsPoint} />
            <span className={classNames(truncateText, { [minimalViewText]: !isFullView })}>
              {namingOptions?.endorsements}{' '}
            </span>
            ({endorsementsCount})
          </>
        ),
        onClickFunction: handleEndorsementsSort
      }
    ],
    [
      showAlphabetExceptAll,
      namingOptions,
      reviewsCount,
      endorsementsCount,
      isFullView,
      handleAlphabetSort,
      handleReviewsSort,
      handleEndorsementsSort
    ]
  )

  const shouldShowDivider = useMemo(() => selectedSort === SortingOptions.ALPHABET, [selectedSort])

  return (
    <div className={container}>
      {sortingOptions.map(({ id, label }) => {
        if (id === 'divider') {
          return (
            <div
              key={id}
              className={classNames(verticalDivider, {
                [verticalDividerHidden]: !shouldShowDivider
              })}
            />
          )
        }

        return (
          <div
            key={id}
            className={selectedSort === id ? activeSwitcher : inactiveSwitcher}
            id={id}
            onClick={onSelectSorting}
          >
            {label}
          </div>
        )
      })}
    </div>
  )
}

export default SortingRatingBar
